import { MutationCache, QueryCache, QueryClient } from "@tanstack/react-query";
import { isApiError } from "./http";

declare module "@tanstack/react-query" {
  interface Register {
    queryMeta: {
      /** Show a toast when this query fails (pages normally render an inline error state instead). */
      errorToast?: boolean;
    };
    mutationMeta: {
      /** Suppress the default error toast (the form shows field errors itself). */
      errorToast?: boolean;
      /** Toast shown on success. */
      successMessage?: string;
    };
  }
}

export interface CreateQueryClientOptions {
  /** Shows an error to the user (the shells pass a localised toast). */
  onError?: (error: unknown) => void;
  onSuccessMessage?: (message: string) => void;
}

/**
 * Query defaults for the platform: 30 s freshness, no refetch on focus (the SignalR channel pushes changes), no retries for
 * client errors, two retries with backoff when the service is momentarily unreachable. Mutation failures toast by default, query
 * failures only when `meta.errorToast` is set.
 */
export function createQueryClient(options: CreateQueryClientOptions = {}): QueryClient {
  return new QueryClient({
    queryCache: new QueryCache({
      onError: (error, query) => {
        if (query.meta?.errorToast) options.onError?.(error);
      },
    }),
    mutationCache: new MutationCache({
      onError: (error, _v, _c, mutation) => {
        if (mutation.meta?.errorToast === false) return;
        if (isApiError(error) && error.isUnauthorized) return;
        options.onError?.(error);
      },
      onSuccess: (_d, _v, _c, mutation) => {
        if (mutation.meta?.successMessage) options.onSuccessMessage?.(mutation.meta.successMessage);
      },
    }),
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        gcTime: 5 * 60_000,
        refetchOnWindowFocus: false,
        retry: (count, error) => {
          if (isApiError(error)) return error.isUnavailable && count < 2;
          return count < 1;
        },
        retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 8000),
      },
      mutations: { retry: false },
    },
  });
}

/** Query-key roots shared by every app so invalidation is consistent. */
export const queryKeys = {
  session: ["session"] as const,
  personas: ["auth", "personas"] as const,
  notifications: (unread?: boolean) => ["notifications", { unread: !!unread }] as const,
  notificationsRoot: ["notifications"] as const,
  publicConsultations: ["public", "consultations"] as const,
  publicConsultation: (slug: string) => ["public", "consultations", slug] as const,
};
